import { z } from 'zod';

// Article validation schema
export const articleSchema = z.object({
  title: z.string()
    .trim()
    .min(1, 'Tittel er påkrevd')
    .max(200, 'Tittel kan ikke være lengre enn 200 tegn'),
  description: z.string()
    .trim()
    .min(1, 'Beskrivelse er påkrevd')
    .max(500, 'Beskrivelse kan ikke være lengre enn 500 tegn'),
  content: z.string()
    .trim()
    .min(1, 'Innhold er påkrevd')
    .max(50000, 'Innhold kan ikke være lengre enn 50000 tegn'),
  tags: z.array(z.string().trim().max(50, 'Tag kan ikke være lengre enn 50 tegn'))
    .max(10, 'Maksimalt 10 tags tillatt'),
  excerpt: z.string()
    .max(300, 'Utdrag kan ikke være lengre enn 300 tegn')
    .optional(),
  min_age: z.number().min(0).max(24),
  max_age: z.number().min(0).max(24),
  reading_time: z.number().min(1),
  is_premium: z.boolean(),
  category: z.enum(['nutrition', 'safety', 'development', 'planning', 'general']),
  content_type: z.enum(['markdown', 'html'])
});

// Food log validation schema
export const foodLogSchema = z.object({
  notes: z.string()
    .max(1000, 'Notater kan ikke være lengre enn 1000 tegn')
    .optional()
    .or(z.literal('')),
  allergy_reaction: z.string()
    .max(500, 'Allergisk reaksjon kan ikke være lengre enn 500 tegn')
    .optional()
    .or(z.literal('')),
  consumed_at: z.string().refine((val) => {
    const date = new Date(val);
    return !isNaN(date.getTime());
  }, 'Ugyldig dato'),
  preference: z.enum(['loved', 'liked', 'neutral', 'disliked', 'refused']).optional()
});

// CSV row validation schema for food bulk upload
export const csvFoodRowSchema = z.object({
  name: z.string()
    .trim()
    .min(1, 'Navn er påkrevd')
    .max(100, 'Navn kan ikke være lengre enn 100 tegn'),
  min_age: z.number()
    .min(0)
    .max(24),
  cutting_method: z.string()
    .trim()
    .min(1, 'Skjæremetode er påkrevd')
    .max(500, 'Skjæremetode kan ikke være lengre enn 500 tegn'),
  consistency: z.string()
    .max(500, 'Konsistens kan ikke være lengre enn 500 tegn')
    .optional(),
  allergy_risk: z.enum(['low', 'medium', 'high']),
  choking_risk: z.enum(['low', 'medium', 'high']),
  preparation: z.string()
    .trim()
    .min(1, 'Tilberedning er påkrevd')
    .max(2000, 'Tilberedning kan ikke være lengre enn 2000 tegn'),
  allergens: z.array(z.string().max(50)),
  category: z.string().max(50),
  is_premium: z.boolean(),
  image_url: z.string().url('Ugyldig URL').optional().or(z.literal(''))
});

// Document content validation
export const documentContentSchema = z.object({
  title: z.string()
    .trim()
    .min(1, 'Tittel er påkrevd')
    .max(200, 'Tittel kan ikke være lengre enn 200 tegn'),
  content: z.string()
    .trim()
    .min(1, 'Innhold er påkrevd')
    .max(100000, 'Innhold kan ikke være lengre enn 100000 tegn'),
  images: z.array(z.string()).optional()
});

// Helper function to sanitize CSV input and detect injection patterns
export const sanitizeCsvValue = (value: string): string => {
  const trimmed = value.trim();
  
  // Check for CSV injection patterns (formulas starting with =, +, -, @)
  if (/^[=+\-@]/.test(trimmed)) {
    // Prepend single quote to neutralize formula
    return `'${trimmed}`;
  }
  
  return trimmed;
};

// Validate time string format (HH:MM)
export const timeStringSchema = z.string().regex(
  /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
  'Ugyldig klokkeslett format (bruk HH:MM)'
);
